import { useEffect, useId, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import mermaid from "mermaid";

type MDRendererProps = {
  content: string;
  className?: string;
};

const MERMAID_STARTERS = [
  "erDiagram",
  "flowchart",
  "graph",
  "sequenceDiagram",
  "classDiagram",
  "stateDiagram",
  "journey",
  "gantt",
  "pie",
  "mindmap",
  "timeline",
  "quadrantChart",
  "requirementDiagram",
  "gitGraph",
  "C4Context",
  "C4Container",
  "C4Component",
  "C4Dynamic",
  "C4Deployment",
] as const;

let mermaidInitialized = false;

const initializeMermaid = () => {
  if (mermaidInitialized) {
    return;
  }

  mermaid.initialize({
    startOnLoad: false,
    securityLevel: "strict",
    theme: "neutral",
  });
  mermaidInitialized = true;
};

const looksLikeMermaid = (value: string): boolean => {
  const firstLine = value.trimStart().split("\n")[0]?.trim() ?? "";
  return MERMAID_STARTERS.some((starter) => firstLine.startsWith(starter));
};

const normalizeMarkdownForMermaid = (content: string): string => {
  const lines = content.split("\n");
  const normalized: string[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const currentLine = lines[index] ?? "";
    const trimmed = currentLine.trim();

    if (!MERMAID_STARTERS.some((starter) => trimmed.startsWith(starter))) {
      normalized.push(currentLine);
      continue;
    }

    const block = [trimmed];
    let cursor = index + 1;

    while (cursor < lines.length) {
      const nextLine = lines[cursor] ?? "";
      const nextTrimmed = nextLine.trim();

      if (!nextTrimmed) {
        block.push("");
        cursor += 1;
        continue;
      }

      const isIndented = /^\s{2,}|\t/.test(nextLine);
      const isDiagramContinuation =
        isIndented ||
        /^[\w"'`[{(]/.test(nextTrimmed) ||
        /^(%%|style\b|classDef\b|class\b|linkStyle\b|subgraph\b|end\b|accTitle:|accDescr:)/.test(
          nextTrimmed,
        );

      if (!isDiagramContinuation) {
        break;
      }

      block.push(nextLine);
      cursor += 1;
    }

    if (block.length > 1) {
      normalized.push("```mermaid", ...block, "```");
      index = cursor - 1;
      continue;
    }

    normalized.push(currentLine);
  }

  return normalized.join("\n");
};

type MermaidDiagramProps = {
  chart: string;
};

function MermaidDiagram({ chart }: MermaidDiagramProps) {
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const id = useId().replace(/:/g, "");

  useEffect(() => {
    let cancelled = false;

    const renderDiagram = async () => {
      try {
        initializeMermaid();
        const { svg: rendered } = await mermaid.render(`mermaid-${id}`, chart);
        if (!cancelled) {
          setSvg(rendered);
          setError(null);
        }
      } catch (renderError) {
        if (!cancelled) {
          setSvg(null);
          setError(
            renderError instanceof Error
              ? renderError.message
              : "Failed to render Mermaid diagram.",
          );
        }
      }
    };

    void renderDiagram();

    return () => {
      cancelled = true;
    };
  }, [chart, id]);

  if (error) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <div className="mb-2 font-medium">Mermaid render failed</div>
        <pre className="overflow-x-auto whitespace-pre-wrap text-xs">{chart}</pre>
      </div>
    );
  }

  if (!svg) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
        Rendering diagram...
      </div>
    );
  }

  return (
    <div
      className="overflow-x-auto rounded-md border border-slate-200 bg-white p-4"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

export function MDRenderer({ content, className }: MDRendererProps) {
  const normalizedContent = normalizeMarkdownForMermaid(content);

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children, ...props }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {children}
            </a>
          ),
          table: ({ children, ...props }) => (
            <div className="querygen-md-table-wrap">
              <table {...props}>{children}</table>
            </div>
          ),
          code: ({ className: codeClassName, children, ...props }) => {
            const code = String(children).replace(/\n$/, "");
            const language = codeClassName?.replace("language-", "").trim().toLowerCase();
            const shouldRenderMermaid =
              language === "mermaid" || (code.includes("\n") && looksLikeMermaid(code));

            if (shouldRenderMermaid) {
              return <MermaidDiagram chart={code} />;
            }

            return (
              <code className={codeClassName} {...props}>
                {children}
              </code>
            );
          },
        }}
      >
        {normalizedContent}
      </ReactMarkdown>
    </div>
  );
}
